import {
  FontColorEnum,
  FontSizeEnum,
  FontWeightEnum,
  IconButtonSection,
  IconSizeEnum,
  ModalDialog,
  ModalMessage,
  ModalMessageColorsEnum,
  PlusIcon,
  Typography,
} from '@acceso/design-system';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { TableWithDetailCard } from '@/components/TableWithDetailCard/TableWithDetailCard';
import { useChannels } from '@/features/broadcasters/api/get-channels';
import { usePatchChannel } from '@/features/broadcasters/api/patch-channel';
import { usePostChannel } from '@/features/broadcasters/api/post-channels';
import { ChannelType } from '@/features/broadcasters/types';
import { FormTypeEnum } from '@/types';
import { getParamsFromTableFilters } from '@/utils/table-filters';

import { ParentBroadcastersDetailCard } from '../components/ParentBroadcastersDetailCard';
import { ParentBroadcastersDrawerForm } from '../components/ParentBroadcastersDrawerForm';
import { ParentBroadcastersTable } from '../components/ParentBroadcastersTable';
import { useParentBroadcastersControllers } from '../hooks/useParentBroadcastersControllers';
import { useParentBroadcastersPermissions } from '../hooks/useParentBroadcastersPermissions';
import { useParentChannelFilters } from '../stores/parent-channel-filters';

export default function ParentBroadcastersPage() {
  const { t } = useTranslation();
  const { parentBroadcasterDrawer, parentBroadcasterDialog, handleDeleteParentBroadcasters } =
    useParentBroadcastersControllers();

  const { filters, paginationValues, clearFilters } = useParentChannelFilters();

  /**
   * Resets table filters on page unmount.
   */
  useEffect(() => {
    return () => {
      clearFilters();
    };
  }, [clearFilters]);

  const {
    data: { data: channels = [], total = 0 } = {},
    isError,
    isFetching,
  } = useChannels({
    params: getParamsFromTableFilters({
      filters: {
        ...filters,
        isParentChannel: true,
      },
      paginationValues,
    }),
  });
  const { mutate: createChannel, isPending: isCreatingChannel } = usePostChannel();
  const { mutate: updateChannel, isPending: isUpdatingChannel } = usePatchChannel();

  const { canCreate, canUpdate, canDuplicate, canDelete } = useParentBroadcastersPermissions();

  const onCreate = () => {
    if (!canCreate) return;
    parentBroadcasterDrawer.open();
  };

  const onUpdate = (parentBroadcaster: ChannelType) => {
    if (!canUpdate) return;
    parentBroadcasterDrawer.open({
      type: FormTypeEnum.UPDATE,
      data: parentBroadcaster,
    });
  };

  const onDuplicate = (parentBroadcaster: ChannelType) => {
    if (!canDuplicate) return;
    parentBroadcasterDrawer.open({
      type: FormTypeEnum.DUPLICATE,
      data: parentBroadcaster,
    });
  };

  const onDelete = (parentBroadcaster: ChannelType) => {
    if (!canDelete) return;
    parentBroadcasterDialog.open(parentBroadcaster);
  };

  return (
    <>
      <div className="flex flex-col">
        <div className="flex justify-between py-size-8">
          <Typography
            fontSize={FontSizeEnum.Xl}
            weight={FontWeightEnum.Bold}
            color={FontColorEnum.Dark10}
          >
            {t('Emisoras madre')}
          </Typography>

          {canCreate && (
            <IconButtonSection
              className="mr-size-16"
              onClick={onCreate}
              internalProps={{
                icon: {
                  size: IconSizeEnum.SM,
                  type: PlusIcon,
                },
                text: {
                  value: t('Nuevo'),
                  color: FontColorEnum.Default,
                },
              }}
            />
          )}
        </div>

        <TableWithDetailCard<ChannelType>
          isError={isError}
          renderTable={({ setData, handleOpenSecondCard, data }) => (
            <ParentBroadcastersTable
              items={channels}
              totalItems={total}
              isFetching={isFetching}
              activeItem={data}
              onUpdate={canUpdate ? onUpdate : undefined}
              onDuplicate={canDuplicate ? onDuplicate : undefined}
              onDelete={canDelete ? onDelete : undefined}
              onSelect={handleOpenSecondCard}
              setData={setData}
            />
          )}
          renderDetailCard={({ data, isSecondCardCollapsed, handleSecondCardCollapse }) => (
            <ParentBroadcastersDetailCard
              parentBroadcaster={data}
              isCollapsed={isSecondCardCollapsed}
              onCollapse={handleSecondCardCollapse}
              onUpdate={onUpdate}
            />
          )}
        />
      </div>

      <ParentBroadcastersDrawerForm
        isOpen={parentBroadcasterDrawer.isOpen}
        onClose={parentBroadcasterDrawer.close}
        onSubmit={data => {
          if (parentBroadcasterDrawer.data?.type === FormTypeEnum.UPDATE) {
            updateChannel(
              {
                ...data,
                id: parentBroadcasterDrawer.data.data.id,
              },
              {
                onSuccess: () => {
                  parentBroadcasterDrawer.close();
                },
              }
            );
          } else {
            createChannel(data, {
              onSuccess: () => {
                parentBroadcasterDrawer.close();
              },
            });
          }
        }}
        data={parentBroadcasterDrawer.data}
        isMutating={isCreatingChannel || isUpdatingChannel}
      />

      <ModalDialog
        isOpen={parentBroadcasterDialog.isOpen}
        onClose={parentBroadcasterDialog.close}
        onAction={handleDeleteParentBroadcasters}
        heading={t('Eliminar emisora madre')}
        actionLabel={t('Eliminar')}
        linkLabel={t('Cancelar')}
        color={ModalMessageColorsEnum.ERROR}
      >
        <ModalMessage
          title={t('¿Estás seguro de querer eliminar la emisora madre seleccionada?')}
        />
      </ModalDialog>
    </>
  );
}
